import { fetchStores } from 'api/Stores/actions'
import type { ChannelSummaryType } from 'ChannelSummary/types'
import classNames from 'classnames'
import { selectChannelsType, selectTabRequestParams } from 'data/selectors'
import type { DetailsData } from 'data/types'
import { GroupDetails } from 'GroupDetails'
import { getDefaultContentType } from 'GroupDetails/utils'
import React, { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import type { State } from 'store/types'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'

import { toggleListDetails } from './actions'
import { selectIsListDetailsOpen } from './selectors'
import useStyles from './useStyles'

interface Props {
  data: DetailsData
  countryId?: string
  channel?: string
  owner?: string
  saleType?: string
  isLoading: boolean
  type: ChannelSummaryType
}

const transitionProps = { unmountOnExit: true }

function ListDetails({ channel, countryId, data, isLoading, owner, saleType, type }: Props) {
  const { t: translate } = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles()
  const channelsType = useSelector(selectChannelsType)
  const isToggled = useSelector(selectIsListDetailsOpen)
  const tabRequestParams = useSelector(selectTabRequestParams)
  const initialContentType = getDefaultContentType({ data, type })
  const isListDetailsOpen = useSelector((state: State) => state.isListDetailsOpen)
  const classesAccordion = {
    root: classes.accordion,
  }
  const classesAccordionSummary = {
    content: classes.accordionSummaryContent,
    expanded: classes.accordionSummaryExpanded,
    root: classes.accordionSummaryRoot,
  }
  const classesAccordionDetails = {
    root: classes.accordionDetails,
  }
  const fetchStoresAction = useCallback(() => {
    dispatch(
      fetchStores({
        ...tabRequestParams,
        isListDetailsOpen,
        channel,
        countryId,
        owner,
        saleType,
        storesType: initialContentType,
        type,
      }),
    )
  }, [dispatch, tabRequestParams, isListDetailsOpen, channel, countryId, owner, saleType, initialContentType, type])
  const onChange = () => {
    if (!isToggled) {
      fetchStoresAction()
    }
    dispatch(toggleListDetails({ channelsType }))
  }

  return (
    <Accordion
      classes={classesAccordion}
      disabled={isLoading}
      expanded={isToggled}
      onChange={onChange}
      TransitionProps={transitionProps}
    >
      <AccordionSummary
        classes={classesAccordionSummary}
        expandIcon={<i className={classNames('fm fm-dropdownlist', classes.expandedIcon)} />}
      >
        {translate('search.view')}
      </AccordionSummary>
      <AccordionDetails classes={classesAccordionDetails}>
        <GroupDetails channel={channel} countryId={countryId} data={data} isLoading={isLoading} owner={owner} type={type} />
      </AccordionDetails>
    </Accordion>
  )
}

export default memo(ListDetails)
